import styled from "styled-components";

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  /* --pi-span is how many columns' worth of the row this card occupies, so the
     width formula stays the same at every breakpoint. The widest breakpoint
     defers to ProductsGrid, which lets a page ask for a different column count
     without every call site knowing the widths. */
  --pi-span: ${(p) => (p.$isPromo ? "1" : "2")};
  --pi-gap: 15px;

  @media (min-width: 1024px) {
    --pi-span: ${(p) => (p.$isPromo ? "1.5" : "3")};
    --pi-gap: 30px;
  }

  @media (min-width: 1400px) {
    --pi-span: ${(p) =>
      p.$isPromo
        ? "var(--products-promo-span-wide, 2)"
        : "var(--products-columns-wide, 4)"};
    --pi-gap: 32px;
  }

  width: calc(
    (100% / var(--pi-span)) - var(--pi-gap) + (var(--pi-gap) / var(--pi-span))
  );
`;

export const Container = styled.div`
  display: ${(p) => (p.$isPromo ? "flex" : "block")};
  align-items: ${(p) => p.$isPromo && "center"};
  position: relative;
  width: 100%;
  min-width: 0;
  background-color: ${(p) => p.$isPromo && "var(--color-bg-2)"};
`;

export const ImageContainer = styled.a`
  flex-shrink: 0;
  display: block;
  width: ${(p) => (p.$isPromo ? "47.4548%" : "100%")};
  /* Gallery images are requested from the API at 604x752. Sizing the box to
     that ratio rather than a fixed height keeps the image exactly as wide as
     its card at every viewport, so the text below starts at the image edge
     instead of at a letterboxed one, and every card in a row matches. */
  aspect-ratio: 604 / 752;
  border-top: ${(p) => p.$isPromo && "2px solid var(--color-bg-2)"};
  border-bottom: ${(p) => p.$isPromo && "2px solid var(--color-bg-2)"};
  border-left: ${(p) => p.$isPromo && "2px solid var(--color-bg-2)"};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const Label = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 10px;
  left: 0;
  color: ${(p) => (p.$fg ? p.$fg : "#000")};
  background-color: ${(p) => (p.$bg ? p.$bg : "#fff")};
  font-weight: 700;
  text-transform: uppercase;

  @media (max-width: 1023px) {
    height: 20px;
    padding: 0 8px;
    font-size: 10px;
    letter-spacing: 0.1px;
  }

  @media (min-width: 1024px) {
    height: 27px;
    padding: 0 12px;
    font-size: 12px;
    letter-spacing: 0.12px;
  }
`;

export const FavouriteContainer = styled.div`
  position: absolute;
  
  @media (max-width: 1023px) {
    top: 8px;
    right: 8px;
  }

  @media (min-width: 1024px) {
    top: 8px;
    right: 8px;
  }
`;

export const AddToButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: none;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (max-width: 1023px) {
    width: 24px;
    height: 24px;
    font-size: 24px;
  }

  @media (min-width: 1024px) {
    width: 32px;
    height: 32px;
    font-size: 32px;
  }
`;

export const AddToCartContainer = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;

  @media (min-width: 1024px) {
    display: none;

    ${Container}:hover & {
      display: block;
    }
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: ${(p) => !p.$isPromo && "13px"};
  padding: ${(p) => (p.$isPromo ? "24px 32px 24px 24px" : "0 16px 0 0")};
  text-align: left;

  /* align-items: flex-start sizes children to fit-content, so a long single-line
     title would otherwise grow past the card and its image. Keep every child
     within the body width and let the text wrap instead. */
  > * {
    max-width: 100%;
    min-width: 0;
  }
`;

export const BrandName = styled.div`
  max-width: 100%;
  color: var(--color-accent);
  overflow-wrap: break-word;
  font-weight: 700;
  text-align: left;
  text-decoration: underline;
  text-transform: uppercase;

  &:hover {
    color: #cb000e;
  }

  @media (max-width: 1023px) {
    font-size: 16px;
  }

  @media (min-width: 1024px) {
    font-size: 12px;
  }
`;

export const BrandImage = styled.div`
  @media (max-width: 1023px) {
    width: 130px;
  }

  @media (min-width: 1024px) {
    width: 200px;
  }
`;

export const ProductName = styled.div`
  display: block;
  max-width: 100%;
  color: inherit;
  overflow-wrap: break-word;
  font-weight: 500;

  @media (max-width: 1023px) {
    margin: 4px 0;
    font-size: 13px;
    line-height: 1.54;
  }

  @media (min-width: 1024px) {
    margin: 8px 0;
    font-size: 18px;
    line-height: 1.38;
  }
`;

export const PointsRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  max-width: 100%;
  /* Cards in a flex row stretch to the tallest card, so pushing the points to
     the bottom keeps them aligned across the row whatever the title height.
     The promo body is vertically centred, so it keeps a plain gap. */
  margin-top: ${(p) => (p.$isPromo ? "8px" : "auto")};
  padding-top: ${(p) => !p.$isPromo && "8px"};
`;
